<template>
  <div class="phone-frame">
    <!-- 瀏海 -->
    <div class="phone-notch"></div>

    <!-- LINE 標題列 -->
    <div class="line-header">
      <i class="ri-arrow-left-s-line"></i>
      <span class="line-header-title">LINE 預覽</span>
      <div class="line-header-actions">
        <i class="ri-phone-line"></i>
        <i class="ri-menu-line"></i>
      </div>
    </div>

    <!-- 聊天內容區 -->
    <div class="chat-area" ref="chatAreaRef">
      <LineChatMessages
        :messages="simulatedMessages"
        welcome-text="您好！歡迎使用我們的服務 😊"
      />

      <!-- 清除模擬按鈕 -->
      <button v-if="simulatedMessages.length" class="btn-clear-sim" @click="$emit('clear-sim')">
        <i class="ri-refresh-line"></i> 清除模擬
      </button>
    </div>

    <!-- 圖文選單區（固定在底部） -->
    <div class="rich-menu-section">
      <!-- 圖文選單主區域 -->
      <div
        v-show="!menuCollapsed"
        class="menu-area"
        :class="[currentTab.heightType === 'half' ? 'height-half' : 'height-full']"
      >
        <img v-if="currentTab.imageUrl" :src="currentTab.imageUrl" class="menu-bg" alt="背景圖" />

        <!-- Tab 切換列 -->
        <div
          v-if="tabs.length > 1"
          class="tab-switch-bar"
          :style="{ height: `${tabBarPct}%` }"
        >
          <button
            v-for="(tab, idx) in tabs"
            :key="idx"
            :class="['tab-switch-btn', { active: activeTab === idx }]"
            @click="$emit('tab-change', idx)"
          >
            <span class="tab-switch-text">{{ tab.tabName || `Tab ${idx + 1}` }}</span>
          </button>
        </div>

        <!-- 格子網格 -->
        <div
          class="cells-grid"
          :class="[
            `layout-${currentTab.layoutType}`,
            currentTab.heightType === 'half' ? 'half-mode' : ''
          ]"
          :style="cellsTopStyle"
        >
          <div
            v-for="(area, idx) in visibleAreas"
            :key="idx"
            class="menu-cell"
            :class="{ 'has-label': area.label || area.actionData, 'pressed': pressedIdx === idx }"
            @click="handleCellClick(area, idx)"
          >
            <div class="cell-inner">
              <span v-if="getClickCount(idx) !== null" class="cell-click-count">
                {{ formatClickCount(getClickCount(idx)) }} 次
              </span>
              <i :class="getActionIcon(area.actionType)"></i>
              <span v-if="cellCaption(area)" class="cell-label" :class="{ 'cell-label-data': isDataCaption(area) }">{{ cellCaption(area) }}</span>
              <span v-else class="cell-empty">未設定</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 選單欄文字（點擊開關圖文選單） -->
      <div class="chat-bar-text" @click="menuCollapsed = !menuCollapsed">
        <i class="ri-menu-2-line"></i>
        <span class="chat-bar-label">{{ currentTab.chatBarText || '查看更多' }}</span>
        <i :class="menuCollapsed ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" class="chat-bar-chevron"></i>
      </div>
    </div>
  </div>
</template>

<script>
import LineChatMessages from '@/components/LineChatMessages.vue';

export default {
  name: 'RichMenuPreview',
  components: { LineChatMessages },
  props: {
    tabs: { type: Array, default: () => [] },
    activeTab: { type: Number, default: 0 },
    tabBarSize: { type: String, default: 'slim' },
    simulatedMessages: { type: Array, default: () => [] },
    clickCounts: { type: Array, default: () => [] },
    /** 與表單「圖文選單預設顯示」一致：true = 進入聊天時展開（預覽顯示選單區），false = 預設收起 */
    menuOpenByDefault: { type: Boolean, default: true }
  },
  emits: ['tab-change', 'cell-click', 'clear-sim'],
  data() {
    return { pressedIdx: null, menuCollapsed: false, _resizeObserver: null };
  },
  mounted() {
    this._setupScrollObserver();
  },
  beforeUnmount() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  },
  computed: {
    currentTab() {
      return this.tabs[this.activeTab] || {
        tabName: 'Tab 1', chatBarText: '查看更多',
        heightType: 'full', layoutType: '6', imageUrl: '', areas: []
      };
    },
    tabBarPct() {
      return ((this.tabBarSize === 'wide' ? 306 : 216) / 1686) * 100;
    },
    cellsTopStyle() {
      if (this.tabs.length <= 1) return { top: '0' };
      return { top: `${this.tabBarPct}%` };
    },
    visibleAreas() {
      const count = parseInt(this.currentTab.layoutType || '6', 10);
      const areas = this.currentTab.areas || [];
      return Array.from({ length: count }, (_, i) => areas[i] || { label: '', actionType: 'uri', actionData: '' });
    }
  },
  watch: {
    simulatedMessages() {
      this._scrollToBottom();
    },
    menuOpenByDefault: {
      immediate: true,
      handler(open) {
        this.menuCollapsed = !open;
      }
    }
  },
  methods: {
    _scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatAreaRef;
        if (el) el.scrollTop = el.scrollHeight;
        // 圖片載入後再捲一次（圖片撐高內容需時間）
        setTimeout(() => {
          if (el) el.scrollTop = el.scrollHeight;
        }, 400);
      });
    },
    _setupScrollObserver() {
      if (typeof ResizeObserver === 'undefined') return;
      // 觀察內容元素（LineChatMessages 根 div），而非捲動容器本身
      // 因為 .chat-area 高度固定（flex:1），只有內容高度會變化
      this.$nextTick(() => {
        const scrollEl = this.$refs.chatAreaRef;
        if (!scrollEl) return;
        const contentEl = scrollEl.firstElementChild; // LineChatMessages root div
        if (!contentEl) return;
        this._resizeObserver = new ResizeObserver(() => {
          scrollEl.scrollTop = scrollEl.scrollHeight;
        });
        this._resizeObserver.observe(contentEl);
      });
    },
    handleCellClick(area, idx) {
      this.pressedIdx = idx;
      setTimeout(() => { this.pressedIdx = null; }, 300);
      if (area.actionType === 'switchTab') {
        const tabIdx = parseInt(area.actionData);
        if (!isNaN(tabIdx)) this.$emit('tab-change', tabIdx);
        return;
      }
      this.$emit('cell-click', area);
    },
    cellCaption(area) {
      if (!area) return '';
      if (area.label) return area.label;
      if (area.actionType === 'share') return '邀請加入官方帳號';
      return area.displayText || area.actionData || '';
    },
    isDataCaption(area) {
      return !area?.label && area?.actionType !== 'share' && !!(area?.displayText || area?.actionData);
    },
    getActionIcon(type) {
      const map = {
        uri: 'ri-external-link-line',
        message: 'ri-chat-1-line',
        postback: 'ri-code-line',
        share: 'ri-user-shared-line',
        switchTab: 'ri-swap-line'
      };
      return map[type] || 'ri-cursor-line';
    },
    getClickCount(areaIndex) {
      const item = this.clickCounts.find(
        count => Number(count.tabIndex) === this.activeTab && Number(count.areaIndex) === areaIndex
      );
      return item ? Number(item.clickCount || 0) : null;
    },
    formatClickCount(value) {
      const count = Number(value || 0);
      if (count >= 10000) return `${Math.floor(count / 1000) / 10}萬`;
      if (count >= 1000) return `${Math.floor(count / 100) / 10}千`;
      return count.toLocaleString('zh-TW');
    }
  }
};
</script>

<style scoped>
/* ── 手機外框 ───────────────────────────────────────── */
.phone-frame {
  width: 320px;
  height: 690px;
  border: 3px solid #1f2937;
  border-radius: 36px;
  background: #E8E5DE;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 32px rgba(0,0,0,.15);
}
.phone-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 18px;
  background: #1f2937;
  border-radius: 0 0 14px 14px;
  z-index: 10;
}

/* ── LINE 標題列 ──────────────────────────────────── */
.line-header {
  display: flex;
  align-items: center;
  padding: 24px 12px 8px;
  background: #06C755;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  flex-shrink: 0;
}
.line-header i { font-size: 18px; }
.line-header-title { flex: 1; }
.line-header-actions { display: flex; gap: 12px; }

/* ── 聊天內容區 ─────────────────────────────────── */
.chat-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #E8E5DE;
}

/* 清除模擬按鈕 */
.btn-clear-sim {
  align-self: center;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #9ca3af;
  background: rgba(0,0,0,.06);
  border: none;
  border-radius: 10px;
  padding: 3px 10px;
  cursor: pointer;
  margin-top: 8px;
}
.btn-clear-sim:hover { color: #ef4444; background: rgba(239,68,68,.08); }

/* ── 圖文選單區（底部） ────────────────────────── */
.rich-menu-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #f0f0f0;
}

/* 選單欄 */
.chat-bar-text {
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #fff;
  font-size: 12px;
  color: #374151;
  font-weight: 500;
  border-top: 1px solid #e5e7eb;
  height: 36px;
  box-sizing: border-box;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  user-select: none;
}
.chat-bar-text:hover { background: #f5f5f5; }
.chat-bar-text > i.ri-menu-2-line { font-size: 14px; color: #9ca3af; position: absolute; left: 12px; }
.chat-bar-label { flex: 1; text-align: center; }
.chat-bar-chevron { font-size: 14px; color: #9ca3af; position: absolute; right: 12px; }

/* Tab 切換列 */
.tab-switch-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  z-index: 2;
  background: rgba(255,255,255,.08);
  border-bottom: 1px solid rgba(255,255,255,.25);
}
.tab-switch-btn {
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  transition: background .12s;
}
.tab-switch-btn::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: transparent;
  transition: background .12s;
}
.tab-switch-btn.active { background: rgba(255,255,255,.18); }
.tab-switch-btn.active::after { background: #06C755; }
.tab-switch-text {
  font-size: 11px;
  color: rgba(255,255,255,.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-weight: 500;
  text-align: center;
  width: 100%;
}
.tab-switch-btn.active .tab-switch-text { color: #111827; font-weight: 600; }

/* 圖文選單主區域 */
.menu-area {
  position: relative;
  width: 100%;
  aspect-ratio: 2500 / 1686;
  background: #d1d5db;
  overflow: hidden;
  flex-shrink: 0;
}
.menu-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}
.menu-area.height-half { aspect-ratio: 2500 / 843; }

/* 格子網格 */
.cells-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  gap: 2px;
  z-index: 1;
}
.cells-grid.layout-1 { grid-template-columns: 1fr; }
.cells-grid.layout-2 { grid-template-columns: 1fr 1fr; }
.cells-grid.layout-3 { grid-template-columns: 1fr 1fr 1fr; }
.cells-grid.layout-4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
.cells-grid.layout-6 { grid-template-columns: 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; }
.cells-grid.layout-8 { grid-template-columns: 1fr 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; }
.cells-grid.half-mode.layout-4 { grid-template-columns: 1fr 1fr 1fr 1fr; grid-template-rows: 1fr; }

/* 格子 */
.menu-cell {
  position: relative;
  z-index: 1;
  border: 2px solid rgba(0,0,0,.82);
  box-shadow: inset 0 0 0 2px rgba(255,255,255,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  transition: background .15s, border-color .15s, box-shadow .15s;
}
.menu-cell:nth-child(1) { background: rgba(59,130,246,.26); }
.menu-cell:nth-child(2) { background: rgba(16,185,129,.26); }
.menu-cell:nth-child(3) { background: rgba(245,158,11,.26); }
.menu-cell:nth-child(4) { background: rgba(236,72,153,.26); }
.menu-cell:nth-child(5) { background: rgba(139,92,246,.26); }
.menu-cell:nth-child(6) { background: rgba(6,182,212,.26); }
.menu-cell:nth-child(7) { background: rgba(249,115,22,.26); }
.menu-cell:nth-child(8) { background: rgba(34,197,94,.26); }
.menu-cell:nth-child(1):hover { background: rgba(59,130,246,.38); }
.menu-cell:nth-child(2):hover { background: rgba(16,185,129,.38); }
.menu-cell:nth-child(3):hover { background: rgba(245,158,11,.38); }
.menu-cell:nth-child(4):hover { background: rgba(236,72,153,.38); }
.menu-cell:nth-child(5):hover { background: rgba(139,92,246,.38); }
.menu-cell:nth-child(6):hover { background: rgba(6,182,212,.38); }
.menu-cell:nth-child(7):hover { background: rgba(249,115,22,.38); }
.menu-cell:nth-child(8):hover { background: rgba(34,197,94,.38); }
.menu-cell:nth-child(1).pressed { background: rgba(59,130,246,.45); }
.menu-cell:nth-child(2).pressed { background: rgba(16,185,129,.45); }
.menu-cell:nth-child(3).pressed { background: rgba(245,158,11,.45); }
.menu-cell:nth-child(4).pressed { background: rgba(236,72,153,.45); }
.menu-cell:nth-child(5).pressed { background: rgba(139,92,246,.45); }
.menu-cell:nth-child(6).pressed { background: rgba(6,182,212,.45); }
.menu-cell:nth-child(7).pressed { background: rgba(249,115,22,.45); }
.menu-cell:nth-child(8).pressed { background: rgba(34,197,94,.45); }
.menu-cell:hover { border-color: rgba(0,0,0,.9); box-shadow: inset 0 0 0 2px rgba(255,255,255,.65); cursor: pointer; }
.menu-cell.pressed { border-color: rgba(0,0,0,.88); box-shadow: inset 0 0 0 2px rgba(255,255,255,.55); transform: scale(.97); transition: transform .15s, background .15s; }

.cell-inner { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 4px 6px; }
.cell-click-count {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(17,24,39,.82);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.3;
  text-shadow: none;
}
.cell-inner i { font-size: 17px; color: rgba(255,255,255,.85); filter: drop-shadow(0 1px 2px rgba(0,0,0,.55)); }
.cell-label { font-size: 15px; font-weight: 600; line-height: 1.25; color: #fff; text-shadow: 0 0 1px rgba(0,0,0,.9), 0 1px 3px rgba(0,0,0,.85), 0 0 10px rgba(0,0,0,.45); text-align: center; word-break: break-all; max-width: 100%; }
.cell-label-data { font-size: 10px; font-weight: 400; font-family: monospace; opacity: .85; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 90%; }
.cell-empty { font-size: 14px; font-weight: 500; color: rgba(255,255,255,.55); text-shadow: 0 1px 2px rgba(0,0,0,.5); }

.menu-cell:not(.has-label) { border-color: rgba(0,0,0,.76); box-shadow: inset 0 0 0 2px rgba(255,255,255,.45); }
.menu-cell:not(.has-label):nth-child(1) { background: rgba(191,219,254,.78); }
.menu-cell:not(.has-label):nth-child(2) { background: rgba(167,243,208,.78); }
.menu-cell:not(.has-label):nth-child(3) { background: rgba(254,243,199,.78); }
.menu-cell:not(.has-label):nth-child(4) { background: rgba(251,207,232,.78); }
.menu-cell:not(.has-label):nth-child(5) { background: rgba(221,214,254,.78); }
.menu-cell:not(.has-label):nth-child(6) { background: rgba(165,243,252,.78); }
.menu-cell:not(.has-label):nth-child(7) { background: rgba(254,215,170,.78); }
.menu-cell:not(.has-label):nth-child(8) { background: rgba(187,247,208,.78); }
.menu-cell:not(.has-label):nth-child(1):hover { background: rgba(191,219,254,.92); }
.menu-cell:not(.has-label):nth-child(2):hover { background: rgba(167,243,208,.92); }
.menu-cell:not(.has-label):nth-child(3):hover { background: rgba(254,243,199,.92); }
.menu-cell:not(.has-label):nth-child(4):hover { background: rgba(251,207,232,.92); }
.menu-cell:not(.has-label):nth-child(5):hover { background: rgba(221,214,254,.92); }
.menu-cell:not(.has-label):nth-child(6):hover { background: rgba(165,243,252,.92); }
.menu-cell:not(.has-label):nth-child(7):hover { background: rgba(254,215,170,.92); }
.menu-cell:not(.has-label):nth-child(8):hover { background: rgba(187,247,208,.92); }
.menu-cell:not(.has-label).pressed:nth-child(1) { background: rgba(147,197,253,.95); }
.menu-cell:not(.has-label).pressed:nth-child(2) { background: rgba(110,231,183,.95); }
.menu-cell:not(.has-label).pressed:nth-child(3) { background: rgba(252,211,77,.95); }
.menu-cell:not(.has-label).pressed:nth-child(4) { background: rgba(244,114,182,.95); }
.menu-cell:not(.has-label).pressed:nth-child(5) { background: rgba(196,181,253,.95); }
.menu-cell:not(.has-label).pressed:nth-child(6) { background: rgba(34,211,238,.95); }
.menu-cell:not(.has-label).pressed:nth-child(7) { background: rgba(251,146,60,.95); }
.menu-cell:not(.has-label).pressed:nth-child(8) { background: rgba(134,239,172,.95); }
.menu-cell:not(.has-label) .cell-empty { color: rgba(55,65,81,.75); text-shadow: none; }
.menu-cell:not(.has-label) .cell-inner i { color: rgba(75,85,99,.85); filter: none; }
</style>
